/**
 * ログ収集サービス
 * バックエンドAPIを通じてKinesis Data Firehoseにログを送信
 *
 * 【改修】Axios → fetch API に変換（Axios 依存を排除）
 */

// ログタイプの定義
export interface OperationLog {
  userId?: number;
  action: string;
  detail: Record<string, unknown>;
  ipAddress?: string;
}

export interface ErrorLog {
  errorType: string;
  errorMessage: string;
  stackTrace?: string;
  userId?: number;
  requestPath?: string;
}

interface BatchLogEntry {
  action: string;
  detail: Record<string, unknown>;
  userId?: number;
  timestamp?: string;
}

class LogService {
  private readonly baseURL: string;
  private batchQueue: BatchLogEntry[] = [];
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly batchSize = 50;
  private readonly batchInterval = 5000; // 5秒

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '/api';
  }

  /**
   * fetch ラッパー（ログ送信用）
   * ログ送信の失敗はアプリケーションの動作を妨げない。
   */
  private async post(endpoint: string, body: unknown): Promise<Response | null> {
    try {
      return await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
    } catch (error) {
      console.error('Log service error:', error);
      return null;
    }
  }

  /**
   * 操作ログを送信
   */
  async sendOperationLog(log: OperationLog): Promise<boolean> {
    try {
      const response = await this.post('/logs/operation', log);
      return response?.status === 201;
    } catch (error) {
      console.error('Failed to send operation log:', error);
      this.addToBatch({
        ...log,
        timestamp: new Date().toISOString()
      });
      return false;
    }
  }

  /**
   * エラーログを送信
   */
  async sendErrorLog(log: ErrorLog): Promise<boolean> {
    try {
      const response = await this.post('/logs/error', log);
      return response?.status === 201;
    } catch (error) {
      console.error('Failed to send error log:', error);
      return false;
    }
  }

  /**
   * バッチログを送信
   */
  async sendBatchLogs(logs: BatchLogEntry[]): Promise<boolean> {
    try {
      const response = await this.post('/logs/batch', { logs });
      return response?.status === 201;
    } catch (error) {
      console.error('Failed to send batch logs:', error);
      return false;
    }
  }

  /**
   * バッチキューにログを追加
   */
  private addToBatch(log: BatchLogEntry): void {
    this.batchQueue.push(log);

    if (this.batchQueue.length >= this.batchSize) {
      this.flushBatch();
    } else if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => this.flushBatch(), this.batchInterval);
    }
  }

  /**
   * バッチキューをフラッシュ
   */
  private async flushBatch(): Promise<void> {
    if (this.batchQueue.length === 0) return;

    const logsToSend = [...this.batchQueue];
    this.batchQueue = [];

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    await this.sendBatchLogs(logsToSend);
  }

  /**
   * ユーザーアクションをログ記録
   */
  logUserAction(
    action: string,
    detail: Record<string, unknown> = {},
    userId?: number
  ): void {
    this.sendOperationLog({
      userId,
      action,
      detail: {
        ...detail,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    });
  }

  logPageView(pageName: string, userId?: number): void {
    this.logUserAction('page_view', { page: pageName, referrer: document.referrer }, userId);
  }

  logClick(elementId: string, elementText: string, userId?: number): void {
    this.logUserAction('click', { elementId, elementText, target: elementId }, userId);
  }

  logFormSubmit(formName: string, success: boolean, userId?: number): void {
    this.logUserAction('form_submit', { formName, success, timestamp: new Date().toISOString() }, userId);
  }

  logApiError(endpoint: string, method: string, statusCode: number, errorMessage: string, userId?: number): void {
    this.sendErrorLog({
      errorType: 'APIError',
      errorMessage,
      requestPath: endpoint,
      userId,
      stackTrace: `${method} ${endpoint} - Status: ${statusCode}`
    });
  }

  logJavaScriptError(error: Error, _context?: string): void {
    this.sendErrorLog({
      errorType: error.name || 'JavaScriptError',
      errorMessage: error.message,
      stackTrace: error.stack,
      requestPath: window.location.pathname,
      userId: this.getCurrentUserId()
    });
  }

  logPerformance(metricName: string, value: number, unit: string = 'ms'): void {
    this.logUserAction('performance_metric', { metric: metricName, value, unit });
  }

  logCustomEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.logUserAction(`custom_${eventName}`, eventData);
  }

  private getCurrentUserId(): number | undefined {
    return undefined;
  }

  destroy(): void {
    this.flushBatch();
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
  }
}

// シングルトンインスタンス
const logService = new LogService();

// ブラウザの終了時に残ったログを送信
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    logService.destroy();
  });

  window.addEventListener('error', (event) => {
    logService.logJavaScriptError(
      new Error(event.message),
      `${event.filename}:${event.lineno}:${event.colno}`
    );
  });

  window.addEventListener('unhandledrejection', (event) => {
    logService.logJavaScriptError(
      new Error(event.reason?.message || event.reason || 'Unhandled Promise Rejection'),
      'Promise Rejection'
    );
  });
}

export default logService;
